import { useState, useEffect } from "react";
import { observer, inject } from "mobx-react";
import ProductView from "./ProductView";
import ProductModalView from "./ProductModalView";
import CrossSellView from "./CrossSellView";

const Product = inject('wooCommerceStore', 'studioStore')(observer(({ product, crossSellProducts, view, wooCommerceStore, studioStore }) => {
  if (!product) return null;

  const [quantity, setQuantity] = useState(0);
  const [inCart, setInCart] = useState(0);
  const [productDisplayType, setProductDisplayType] = useState("");
  const [selectedVariation, setSelectedVariation] = useState(() => {
    if (product.type === "variable") {
      const publishedVariations = product.variations.filter((v) => v.status === "publish");
      return publishedVariations.length === 1 ? publishedVariations[0] : null;
    }
    return null;
  });
  const [productWeightQuantity, setProductWeightQuantity] = useState(null);
  const [productClasses, setProductClasses] = useState(new Set([
    "product-card",
    "h-full",
    "bg-white",
  ]));

  const productToAdd = selectedVariation || product;

  const crosssellProduct = product.cross_sell_ids?.length && crossSellProducts
    ? crossSellProducts.find(p => p.id === product.cross_sell_ids[0]) || null
    : null;

  const cart = wooCommerceStore.cart;
  const cartVersion = wooCommerceStore.cartVersion;

  useEffect(() => {
    const cartItem = cart.find((item) => item.id === productToAdd.id);
    if (cartItem) {
      setInCart(cartItem.quantity);
      setQuantity(cartItem.quantity);
    } else {
      setInCart(0);
      setQuantity(0);
    }
  }, [cart, productToAdd.id, cartVersion]);

  useEffect(() => {
    if (studioStore && studioStore.products) {
      const borderClasses = new Set([
        studioStore.products.border_width.border_width_top,
        studioStore.products.border_width.border_width_left,
        studioStore.products.border_width.border_width_right,
        studioStore.products.border_width.border_width_bottom,
        studioStore.products.border_radius,
      ].filter(Boolean));

      setProductClasses(prevClasses => new Set([...prevClasses, ...borderClasses]));
    }
  }, [studioStore]);

  useEffect(() => {
    if (product.type === "variable") {
      // Filter variations with status "publish"
      const publishedVariations = product.variations.filter((v) => v.status === "publish");
      // Only set the variation if we don't have one selected or if there's exactly one variation
      if (!selectedVariation || publishedVariations.length === 1) {
        setSelectedVariation(publishedVariations[0]);
      }
    }
  }, [product.type, product.variations, selectedVariation]);

  useEffect(() => {
    if (selectedVariation?.weight_quantity_based !== productWeightQuantity) {
      setProductWeightQuantity(selectedVariation?.weight_quantity_based ?? null);
    }
  }, [selectedVariation, productWeightQuantity]);

  const handleClearFromCart = () => {
    wooCommerceStore.removeFromCart(productToAdd.id);
  };

  const handleAddToCart = () => {
    const maxQuantity = productToAdd.max_cart_quantity ? parseInt(productToAdd.max_cart_quantity) : null;
    if (maxQuantity !== null && quantity + 1 > maxQuantity) {
      wooCommerceStore.quantityError = `Maximum quantity allowed for this product is ${maxQuantity}`;
      setTimeout(() => {
        wooCommerceStore.quantityError = null;
      }, 3000);
      return;
    }

    if (selectedVariation) {
      productToAdd.parent_name = product.name;
      productToAdd.parent_images = product.images;
      productToAdd.categories = product.categories;
      if (!productToAdd?.type) {
        productToAdd.type = product.type;
      }
    }
    const success = wooCommerceStore.addToCart(productToAdd, 1);
    if (success) {
      setQuantity(prev => prev + 1);
      setInCart(prev => prev + 1);
    }
  };

  const handleRemoveFromCart = () => {
    if (quantity > 0) {
      wooCommerceStore.updateCartItemQuantity(productToAdd.id, quantity - 1);
      setQuantity(prev => prev - 1);
      setInCart(prev => prev - 1);
    } else {
      setInCart(0);
    }
  };

  const handleUpdateQuantity = (newQuantity) => {
    const maxQuantity = productToAdd.max_cart_quantity ? parseInt(productToAdd.max_cart_quantity) : null;
    if (maxQuantity !== null && newQuantity > maxQuantity) {
      wooCommerceStore.quantityError = `Maximum quantity allowed for this product is ${maxQuantity}`;
      setTimeout(() => {
        wooCommerceStore.quantityError = null;
      }, 3000);
      return;
    }

    const success = wooCommerceStore.updateCartItemQuantity(productToAdd.id, newQuantity);
    if (success) {
      setQuantity(newQuantity);
      setInCart(newQuantity);
    }
  };

  const handleVariationChange = (e) => {
    const variationId = parseInt(e.target.value, 10); // Convert to number
    const publishedVariations = product.variations.filter((v) => v.status === "publish");
    const variation = publishedVariations.find((v) => v.id === variationId);
    if (variation) {
      const { weight_quantity_based: productWeightQuantity } = variation;
      setProductWeightQuantity(productWeightQuantity);
      setSelectedVariation(variation);
    }
  };

  const img = selectedVariation?.image?.src ? selectedVariation.image : (product?.images?.[0] ?? {});

  const productType = product?.type ?? "";

  return view === 'modal' ? (
    <ProductModalView
      product={product}
      inCart={inCart}
      img={img}
      handleRemoveFromCart={handleRemoveFromCart}
      handleAddToCart={handleAddToCart}
      handleClearFromCart={handleClearFromCart}
      handleUpdateQuantity={handleUpdateQuantity}
      productWeightQuantity={productWeightQuantity}
      quantity={quantity}
      selectedVariation={selectedVariation}
      setSelectedVariation={setSelectedVariation}
      productType={productType}
      productDisplayType={productDisplayType}
      setProductDisplayType={setProductDisplayType}
      handleVariationChange={handleVariationChange}
    />
  ) : view === 'crosssell' ? (
    <CrossSellView
      product={product}
      inCart={inCart}
      img={img}
      handleClick={() => wooCommerceStore.showProductModal(product)}
      selectedVariation={selectedVariation}
      setSelectedVariation={setSelectedVariation}
      productType={productType}
      productDisplayType={productDisplayType}
      setProductDisplayType={setProductDisplayType}
      handleRemoveFromCart={handleRemoveFromCart}
      handleAddToCart={handleAddToCart}
      handleClearFromCart={handleClearFromCart}
      handleUpdateQuantity={handleUpdateQuantity}
      productWeightQuantity={productWeightQuantity}
      quantity={quantity}
      handleVariationChange={handleVariationChange}
      studioStore={studioStore}
    />
  ) : (
    <ProductView
      product={product}
      productClasses={productClasses}
      inCart={inCart}
      img={img}
      handleClick={() => wooCommerceStore.showProductModal(product)}
      selectedVariation={selectedVariation}
      setSelectedVariation={setSelectedVariation}
      productType={productType}
      productDisplayType={productDisplayType}
      setProductDisplayType={setProductDisplayType}
      handleRemoveFromCart={handleRemoveFromCart}
      handleAddToCart={handleAddToCart}
      handleClearFromCart={handleClearFromCart}
      handleUpdateQuantity={handleUpdateQuantity}
      productWeightQuantity={productWeightQuantity}
      quantity={quantity}
      handleVariationChange={handleVariationChange}
      crosssellProduct={crosssellProduct}
      studioStore={studioStore}
    />
  );
}));

export default Product;
